import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Table from '../components/Table';
import SearchBar from '../components/SearchBar';
import Pagination from '../components/Pagination';
import { membersApi } from '../services/api';
import { badgeStyles, colors, utilityStyles } from '../styles/sharedStyles';

const MemberManagement = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [addError, setAddError] = useState('');
  const [newMember, setNewMember] = useState({
    firstName: '',
    lastName: '',
    email: '',
    membershipNumber: ''
  });

  // Filters
  const [filters, setFilters] = useState({
    specialty: '',
    status: '',
    membershipType: '',
  });

  // Fetch members
  const fetchMembers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await membersApi.getAll({
        page: currentPage,
        limit: 10,
        search: searchQuery,
        ...filters,
      });
      
      const responseData = response.data?.members || response.data?.data || response.data || [];
      setMembers(Array.isArray(responseData) ? responseData : []);
      setTotalPages(response.data.totalPages || 1);
      setTotalMembers(response.data.totalMembers || responseData.length || 0);
    } catch (err) {
      console.error('Error fetching members:', err);
      setError('Failed to load members. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [currentPage, searchQuery, filters]);

  // Handle search
  const handleSearch = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  // Handle filter change
  const handleFilterChange = (filterName, value) => {
    setFilters({ ...filters, [filterName]: value });
    setCurrentPage(1);
  };

  // Handle row click - navigate to member details
  const handleRowClick = (member) => {
    const memberId = member.id || member._id;
    navigate(`/members/${memberId}`);
  };

  // Handle adding existing member
  const handleAddExistingMember = async (e) => {
    e.preventDefault();
    setAddingMember(true);
    setAddError('');

    try {
      await membersApi.createExisting(newMember);
      alert('Member account created successfully! A welcome email has been sent.');
      setShowAddModal(false);
      setNewMember({ firstName: '', lastName: '', email: '', membershipNumber: '' });
      fetchMembers();
    } catch (err) {
      console.error('Error adding member:', err);
      setAddError(err.response?.data?.message || 'Failed to create member account');
    } finally {
      setAddingMember(false);
    }
  };

  // Table columns with fallbacks for snake_case/camelCase keys
  const columns = [
    {
      header: 'Membership #',
      accessor: 'membershipnumber',
      render: (value, member) => {
        const num = value || member.membershipNumber || member.membership_number || 'N/A';
        return <strong style={{ color: colors.primary }}>{num}</strong>;
      },
    },
    {
      header: 'Name',
      accessor: 'first_name',
      render: (_, member) => {
        const first = member.first_name || member.firstName || '';
        const middle = member.middle_name || member.middleName || '';
        const last = member.last_name || member.lastName || '';
        return `${first} ${middle} ${last}`.trim() || member.name || 'N/A';
      },
    },
    {
      header: 'Email',
      accessor: 'email',
      render: (value) => value || 'N/A',
    },
    {
      header: 'Phone',
      accessor: 'phone_number',
      render: (_, member) => member.phone_number || member.phoneNumber || member.phone || 'N/A',
    },
    {
      header: 'Specialty',
      accessor: 'area_of_specialty',
      render: (_, member) => member.area_of_specialty || member.areaOfSpecialty || 'N/A',
    },
    {
      header: 'Type',
      accessor: 'membership_type',
      render: (_, member) => {
        const type = member.membership_type || member.membershipType || 'existing';
        return (
          <span
            style={{
              ...badgeStyles.base,
              ...(type === 'new' ? badgeStyles.success : badgeStyles.info),
            }}
          >
            {type}
          </span>
        );
      },
    },
    {
      header: 'Status',
      accessor: 'is_active',
      render: (_, member) => {
        const active = member.is_active !== undefined ? member.is_active : (member.isActive !== undefined ? member.isActive : true);
        return (
          <span
            style={{
              ...badgeStyles.base,
              ...(active ? badgeStyles.success : badgeStyles.danger),
            }}
          >
            {active ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
  ];

  return (
    <Layout title="Member Management">
      <div style={{ ...utilityStyles.marginBottom20 }}>
        <div
          style={{
            ...utilityStyles.flexRow,
            ...utilityStyles.spaceBetween,
            ...utilityStyles.alignCenter,
            ...utilityStyles.marginBottom16,
          }}
        >
          <SearchBar
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by name, email, or membership number..."
            style={{ maxWidth: '500px' }}
          />
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: colors.primary,
              color: colors.white,
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '14px'
            }}
          >
            + Add Existing Member
          </button>
        </div>

        {/* Filters */}
        <div style={{ ...utilityStyles.flexRow, ...utilityStyles.gap12, flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: colors.textSecondary }}>
              Specialty
            </label>
            <select
              value={filters.specialty}
              onChange={(e) => handleFilterChange('specialty', e.target.value)}
              style={{ padding: '8px 12px', fontSize: '14px', border: `1px solid ${colors.border}`, borderRadius: '4px', backgroundColor: colors.white }}
            >
              <option value="">All Specialties</option>
              <option value="General Surgery">General Surgery</option>
              <option value="Urology">Urology</option>
              <option value="Pediatric Urology">Pediatric Urology</option>
              <option value="Urologic Oncology">Urologic Oncology</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: colors.textSecondary }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{ padding: '8px 12px', fontSize: '14px', border: `1px solid ${colors.border}`, borderRadius: '4px', backgroundColor: colors.white }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: colors.textSecondary }}>
              Membership Type
            </label>
            <select
              value={filters.membershipType}
              onChange={(e) => handleFilterChange('membershipType', e.target.value)}
              style={{ padding: '8px 12px', fontSize: '14px', border: `1px solid ${colors.border}`, borderRadius: '4px', backgroundColor: colors.white }}
            >
              <option value="">All Types</option>
              <option value="new">New</option>
              <option value="existing">Existing</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <Table
        columns={columns}
        data={members}
        loading={loading}
        onRowClick={handleRowClick}
        emptyMessage="No members found"
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={totalMembers}
        itemsPerPage={10}
      />

      {/* Add Existing Member Modal */}
      {showAddModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
          }}
          onClick={() => setShowAddModal(false)}
        >
          <div
            style={{ backgroundColor: colors.white, borderRadius: '8px', padding: '24px', width: '100%', maxWidth: '500px', margin: '20px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: colors.primary }}>Add Existing Member</h2>
              <button
                onClick={() => setShowAddModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: colors.textSecondary }}
              >
                ×
              </button>
            </div>

            <p style={{ color: colors.textSecondary, marginBottom: '20px', fontSize: '14px' }}>
              Create an account for an existing NAUS member. They will receive a welcome email with login credentials.
            </p>

            {addError && (
              <div style={{ padding: '12px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '16px', fontSize: '14px' }}>
                {addError}
              </div>
            )}

            <form onSubmit={handleAddExistingMember}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>First Name *</label>
                <input
                  type="text"
                  value={newMember.firstName}
                  onChange={(e) => setNewMember({ ...newMember, firstName: e.target.value })}
                  required
                  style={{ width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Last Name *</label>
                <input
                  type="text"
                  value={newMember.lastName}
                  onChange={(e) => setNewMember({ ...newMember, lastName: e.target.value })}
                  required
                  style={{ width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Email Address *</label>
                <input
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  required
                  style={{ width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Membership Number *</label>
                <input
                  type="text"
                  value={newMember.membershipNumber}
                  onChange={(e) => setNewMember({ ...newMember, membershipNumber: e.target.value })}
                  required
                  placeholder="e.g., 2024001"
                  style={{ width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{ padding: '10px 20px', backgroundColor: colors.background, color: colors.textPrimary, border: `1px solid ${colors.border}`, borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingMember}
                  style={{ padding: '10px 20px', backgroundColor: addingMember ? colors.textSecondary : colors.primary, color: colors.white, border: 'none', borderRadius: '4px', cursor: addingMember ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500' }}
                >
                  {addingMember ? 'Creating...' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default MemberManagement;